import { useState } from "react";
import { NotebookPen, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/States";

const today = () => new Date().toISOString().slice(0, 10);

export default function DailyLog({ projects, range }) {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const logs = useFetch(`/work-logs?start=${range.start}&end=${range.end}`);
  const [f, setF] = useState({ project_id: "", date: today(), text: "", summary: "" });
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const tidy = async () => {
    setBusy("tidy");
    try { const { data } = await api.post("/work-logs/tidy", { text: f.text, project_name: projects.find((p) => p.id === f.project_id)?.name || "" }); setF({ ...f, text: data.text, summary: data.summary }); toast.success("Tidied up. Check it reads right, then save."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy("save");
    try { await api.post("/work-logs", f); toast.success("Daily log saved"); setF({ ...f, text: "", summary: "" }); logs.reload(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(""); }
  };
  const del = async (l) => { try { await api.delete(`/work-logs/${l.id}`); logs.reload(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <section className="rounded-2xl border bg-card p-6" data-testid="daily-log-card">
      <div className="flex items-start justify-between gap-4"><div><p className="eyebrow">Daily log</p><p className="mt-1 text-sm text-muted-foreground">What got done today, in your words. It feeds the invoice line for that day.</p></div><NotebookPen className="h-5 w-5 text-muted-foreground" /></div>
      <form onSubmit={save} className="mt-5 space-y-3" data-testid="daily-log-form">
        <div className="grid gap-3 sm:grid-cols-[1fr_170px]">
          <select required value={f.project_id} onChange={set("project_id")} className="h-10 rounded-md border bg-background px-3 text-sm" aria-label="Project" data-testid="log-project-select"><option value="">Pick a project…</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <Input type="date" required value={f.date} onChange={set("date")} aria-label="Date" data-testid="log-date-input" />
        </div>
        <Textarea required rows={4} placeholder="e.g. Framed the bunkie back wall, set 6 windows, cleaned up site. Short on 2x6 — need 12 more." value={f.text} onChange={set("text")} maxLength={4000} data-testid="log-text-input" />
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div className="space-y-1"><Label htmlFor="log-summary" className="text-xs">Invoice line (optional)</Label><Input id="log-summary" placeholder="One line for the invoice" value={f.summary} onChange={set("summary")} maxLength={240} data-testid="log-summary-input" /></div>
          <Button type="button" variant="outline" className="rounded-full" onClick={tidy} disabled={!f.text.trim() || !!busy} data-testid="log-tidy-button">{busy === "tidy" ? <Spinner /> : <Sparkles className="mr-2 h-4 w-4" />}Tidy with AI</Button>
          <Button type="submit" className="rounded-full" disabled={!!busy || !f.project_id} data-testid="log-save-button">{busy === "save" ? <Spinner /> : "Save log"}</Button>
        </div>
      </form>
      <div className="mt-6 space-y-2" data-testid="daily-log-list">
        {(logs.data || []).length === 0 ? <p className="text-sm text-muted-foreground" data-testid="daily-log-empty">No logs this week yet.</p> : (logs.data || []).map((l) => (
          <article key={l.id} className="rounded-lg border bg-background p-3 text-sm" data-testid={`log-${l.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="font-mono text-xs text-muted-foreground">{l.date} · {l.project_name}{admin ? ` · ${l.user_name}` : ""}</p>
              {(admin || l.user_id === user.id) && <button type="button" onClick={() => del(l)} aria-label="Delete log" className="text-muted-foreground hover:text-destructive" data-testid={`log-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            {l.summary && <p className="mt-1 font-medium" data-testid={`log-summary-${l.id}`}>{l.summary}</p>}
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{l.text}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
